import "./theme/main.css";
import React, { Component, useEffect, useState } from "react";
import _ from "lodash";
import { useAlert } from "react-alert";
import firebase from "firebase/app";
import "firebase/auth";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { ThemeProvider } from "@material-ui/styles";
import queryString from "query-string";

import { useDispatch, useSelector } from "react-redux";

import CohortPageWithNavigation from "./personas";

import ProjectsPage from "./projects";

import NewTransformationPage from "./transforms/NewTransformationPage";
import TransformationPage from "./transforms/TransformationPage";

import { MASQUERADE_LOCAL_STORAGE_KEY } from "./util";
import { DestinationOAuth } from "./oauth";
import DestinationsListPage from "./DestinationsListPage";
import DestinationConfigPage from "./destinations/DestinationConfigPage";
import AcceptInvitePage from "./AcceptInvitePage";
import EventsPage from "./EventsPage";
import LiveViewPage from "./LiveViewPage";
import VisualEditor from "./VisualEditor";
import SourcesPage from "./SourcesPage";
import SignInScreen from "./SignInScreen";
import TeamPage from "./TeamPage";
import MasqueradePage from "./MasqueradePage";
import PHIAllowListPage from "./PHIAllowListPage";
import { theme } from "./theme";
import { saasDestinations } from "./destinations/destinations";
import WarehousesPage from "./DashboardPage";
import { fetchEventDefs } from "./actions/eventDefs";
import useAccountMetadata from "./hooks/useAccountMetadata";
import { fetchAccountMetadata } from "./actions/environments";
import useOnEnvironmentSet from "./hooks/on-environment-set";
import ObjectSourcesPage from "./ObjectSourcesPage";

function NoMatch() {
  return (
    <h3 style={{ paddingLeft: "15px", paddingRight: "15px" }}>
      404 No Page Found
    </h3>
  );
}

function EntryPage() {
  const { eventDefs: events } = useSelector((state) => state.eventDefs);

  if (events === null) {
    return null;
  } else if (events.length === 0) {
    return <Redirect to={{ pathname: "/sources" }} />;
  } else {
    return <Redirect to={{ pathname: "/events/schema" }} />;
  }
}

export function LoginGatedPaths(props) {
  if (!props.isSignedIn) {
    return (
      <Redirect
        to={`/login?${queryString.stringify({
          next: props.location.pathname + props.location.search,
        })}`}
      />
    );
  } else {
    return <LoginGatedPathsHelper />;
  }
}

var snippetLoaded = false;

// loadFreshpaintSnippetOnce loads freshpaint.js. This function makes sure
// freshpaint.js is only loaded once.
function loadFreshpaintSnippetOnce(config, callback = () => {}) {
  if (!snippetLoaded) {
    // prettier-ignore
    // eslint-disable-next-line
    (function(f,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,c=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?m[1]:null};c&&d(c,"fpState")&&(j=JSON.parse(decodeURIComponent(d(c,"fpState"))),"fpeditor"===j.action&&(b.sessionStorage.setItem("_fpcehash",c),history.replaceState(j.desiredHash||"",f.title,k.pathname+k.search)))}catch(n){}var l,h;window.freshpaint=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments, 0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="freshpaint";e.people=e.people||[];e.toString=function(b){var a="freshpaint";"freshpaint"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" "); for(h=0;h<l.length;h++)c(e,l[h]);var f="set set_once union unset remove delete".split(" ");e.get_group=function(){function a(c){b[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));e.push([d,call2])}}for(var b={},d=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<f.length;c++)a(f[c]);return b};a._i.push([b,d,g])};a.__SV=1.3;b=f.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof FRESHPAINT_CUSTOM_LIB_URL? FRESHPAINT_CUSTOM_LIB_URL:"//perfalytics.com/static/js/freshpaint.js";d=f.getElementsByTagName("script")[0];d.parentNode.insertBefore(b,d)}})(document,window.freshpaint||[]);

    window.freshpaint.init("82ef97c4-8367-4d61-b0be-261498e9dd13", {
      integrations: {
        FullStory:
          !config.hipaa &&
          firebase.auth().currentUser.providerData[0].providerId !==
            firebase.auth.GithubAuthProvider.PROVIDER_ID,
      },
    });

    callback();

    snippetLoaded = true;
  }
}

// LoginGatedPathsHelper is a subcomponent of LoginGatedPath. It has to be a
// seperate component because we want to call hooks in LoginGatedPathsHelper
// that we won't always call in LoginGatedPaths. React requires that hooks called in a component are always called.
function LoginGatedPathsHelper() {
  const alert = useAlert();
  const [currentMasquerade, _setCurrentMasquerade] = useState(
    window.localStorage.getItem(MASQUERADE_LOCAL_STORAGE_KEY)
  );

  const [user, setUser] = useState(null);
  const accountMetadata = useAccountMetadata();
  const dispatch = useDispatch();

  useOnEnvironmentSet(() => {
    dispatch(fetchEventDefs());
  });

  useEffect(() => {
    if (accountMetadata && user) {
      loadFreshpaintSnippetOnce(accountMetadata.config, () =>
        window.freshpaint.identify(user.email)
      );
    }
  }, [accountMetadata, user]);

  // Force reloading the page when a user masquerades in another tab.
  useEffect(() => {
    window.addEventListener("storage", () => {
      const newValue = window.localStorage.getItem(
        MASQUERADE_LOCAL_STORAGE_KEY
      );
      if (currentMasquerade !== newValue) {
        window.location.reload();
      }
    });
  });

  useEffect(() => {
    const unregisterAuthObserver = firebase
      .auth()
      .onAuthStateChanged((user) => {
        setUser(user);
        dispatch(fetchAccountMetadata());
      });

    return () => {
      // Make sure we un-register Firebase observers when the component unmounts.
      unregisterAuthObserver();
    };
  }, [alert, dispatch]);

  return (
    <div className="Body">
      <Switch>
        <Route
          exact={true}
          path={"/events"}
          render={() => <Redirect to={{ pathname: "/events/schema" }} />}
        />
        <Route
          path={"/events/schema/:eventDefID?"}
          exact={true}
          component={EventsPage}
        />
        <Route path={"/sources"} component={SourcesPage} />
        <Route path={"/object-sources"} component={ObjectSourcesPage} />
        <Route path={"/masquerade"} component={MasqueradePage} />
        <Route path={"/settings/team"} component={TeamPage} />
        <Route path={"/settings/projects"} component={ProjectsPage} />
        <Route path={"/invite"} component={AcceptInvitePage} />
        <Route path={"/"} exact={true} component={EntryPage} />
        <Route path={"/events/editor"} component={VisualEditor} />
        <Route path={"/events/liveview"} component={LiveViewPage} />
        <Route path={"/hipaa/allowlist"} component={PHIAllowListPage} />

        {/* TRANSFORMATIONS */}
        <Route
          path={"/transformations/destination/:destination"}
          exact={true}
          component={TransformationPage}
        />
        <Route
          path={"/transformations/event/:fpEventId"}
          exact={true}
          component={TransformationPage}
        />
        <Route
          path={"/transformations/new/:intention"}
          exact={true}
          component={NewTransformationPage}
        />
        <Route
          path={"/transformations/new"}
          exact={true}
          component={NewTransformationPage}
        />
        <Route
          path={"/transformations/edit/:transformationSetId"}
          component={TransformationPage}
        />
        <Route path={"/transformations"} component={TransformationPage} />

        {/* DESTINATIONS */}
        <Route
          path={"/destinations"}
          exact={true}
          component={DestinationsListPage}
        />

        <Route
          path={"/destinations/warehouses"}
          exact={true}
          component={WarehousesPage}
        />
        <Route
          path={"/destinations/apps"}
          exact={true}
          render={() => <DestinationsListPage filter="app" />}
        />
        <Route
          path={"/destinations/advertising"}
          exact={true}
          render={() => <DestinationsListPage filter="advertising" />}
        />
        <Route
          path={"/facebook-pixel-oauth/"}
          exact={true}
          render={() =>
            accountMetadata ? (
              <DestinationOAuth
                destinationURL="/destinations/advertising/facebook-pixel"
                destinationName="facebook-pixel"
              />
            ) : null
          }
        />
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/destinations/cohorting"}
            exact={true}
            render={() =>
              accountMetadata ? (
                <DestinationsListPage filter="cohorting" />
              ) : null
            }
          />
        )}
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/cohorts"}
            exact={true}
            render={() =>
              accountMetadata ? <CohortPageWithNavigation /> : null
            }
          />
        )}
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/cohort/"}
            exact={true}
            render={() =>
              accountMetadata ? <CohortPageWithNavigation /> : null
            }
          />
        )}
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/cohort/:cohortId"}
            exact={true}
            render={() =>
              accountMetadata ? <CohortPageWithNavigation /> : null
            }
          />
        )}
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/facebook-custom-audiences-oauth/"}
            exact={true}
            render={() =>
              accountMetadata ? (
                <DestinationOAuth
                  destinationURL="/destinations/cohorting/facebook-custom-audiences"
                  destinationName="facebook-custom-audiences"
                />
              ) : null
            }
          />
        )}
        {(accountMetadata === null || accountMetadata.config.personas) && (
          <Route
            path={"/google-ads-remarketing-lists-oauth/"}
            exact={true}
            render={() =>
              accountMetadata ? (
                <DestinationOAuth
                  destinationURL="/destinations/cohorting/google-ads-remarketing-lists"
                  destinationName="google-ads-remarketing-lists"
                />
              ) : null
            }
          />
        )}

        {_.map(saasDestinations, (destination) => {
          const path = destination.configPageUrl();
          return (
            <Route
              key={path}
              path={path}
              exact={false}
              render={() => (
                <DestinationConfigPage destinationSlug={destination.slug} />
              )}
            />
          );
        })}
        <Route component={NoMatch} status={404} />
      </Switch>
    </div>
  );
}

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      token: null,
    };
  }

  render() {
    const hostname = window && window.location && window.location.hostname;
    if (hostname === "perfalytics.com") {
      window.location.href = "https://freshpaint.io";
      return null;
    }

    if (this.state.isSignedIn === undefined) {
      return null;
    }

    return (
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Switch>
            <Route
              path="/login/sso/:providerName"
              render={(props) => (
                <SignInScreen
                  {...props}
                  isSignedIn={this.state.isSignedIn}
                  ssoOnly={true}
                />
              )}
            />
            <Route
              path="/login"
              render={(props) => (
                <SignInScreen {...props} isSignedIn={this.state.isSignedIn} />
              )}
            />
            <Route
              render={(props) => (
                <LoginGatedPaths
                  {...props}
                  isSignedIn={this.state.isSignedIn}
                />
              )}
            />
          </Switch>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  componentDidMount() {
    this.unregisterAuthObserver = firebase.auth().onAuthStateChanged((user) => {
      this.setState({ isSignedIn: !!user });
    });
  }

  // Make sure we un-register Firebase observers when the component unmounts.
  componentWillUnmount() {
    if (this.unregisterAuthObserver) {
      this.unregisterAuthObserver();
    }
  }
}

export default App;
